import { LoginForm, MfaForm } from '@community_dev/pixels'
import { useMutation } from '@tanstack/react-query'
import { ReactNode, useState, type JSX } from 'react'
import styled from 'styled-components'

import { postLogin, postMfaCode } from 'api/login'
import { SlateLoginForm } from 'containers/SlateLoginForm'
import { useUserContext } from 'contexts/UserProvider'
import { BoxFormLayout } from 'layouts/BoxFormLayout'

type LoginErrorDetails = {
  field: string
  // the API is inconsistent here:
  message?: string
  // or
  detail?: string
}

type LoginError = {
  name: string
  status: number
  errors?: LoginErrorDetails[]
}

export const StyledLink = styled.a`
  text-decoration: none;
  color: ${({ theme }) => theme?.COLORS.LINKS};
`

const dashboardUrl = import.meta.env.VITE_APP_DASHBOARD_URL

export const LoginScreen = (): JSX.Element => {
  const { setToken, mfaToken, setMfaToken } = useUserContext()
  const [loginErrors, setLoginErrors] = useState<string[]>([])
  const [mfaError, setMfaError] = useState<string | null>(null)
  const [isSlateTimeout, setIsSlateTimeout] = useState(false)
  const [isMfaSetupError, setIsMfaSetupError] = useState(false)
  const { mutate: submitMfaCode, isLoading: isLoadingMfa } = useMutation(postMfaCode, {
    onError(err: { status: number }) {
      if (err.status === 401) {
        setMfaError('Unable to authenticate the code. Please try again.')
      }
    },
    onSuccess({ token }) {
      setMfaToken('')
      setToken(token)
    },
  })
  const { mutate: submitCredentials, isLoading: isLoadingLogin } = useMutation(postLogin, {
    onError(err: LoginError) {
      // MFA is enabled but not activated by the user:
      if (err.status === 409) {
        setIsMfaSetupError(true)
      } else if (err.status === 401) {
        setLoginErrors(['Invalid email or password'])
      } else if (err.status >= 500) {
        setLoginErrors(['Our systems are currently experiencing trouble, try again in a few seconds'])
      } else {
        setLoginErrors((err.errors || []).map((err) => err.message || err.detail || 'unknown error'))
      }
    },
    async onSuccess(rawResp) {
      const res = await rawResp.json()
      // MFA is enabled and activated:
      if (rawResp.status === 206) {
        setMfaToken(res.token)
      } else {
        setToken(res.token)
      }
    },
  })

  function onSubmitMfaCode({ totp }) {
    submitMfaCode({ totp, mfaToken })
  }

  function getErrors(): ReactNode[] {
    if (isMfaSetupError) {
      return [
        <>
          To keep your account safe, we have enabled two-factor authentication. Please{' '}
          <StyledLink href={`${dashboardUrl}/logout`} rel="noreferrer" target="_blank">
            log into the Community Dashboard
          </StyledLink>{' '}
          to set up two-factor authentication.
        </>,
      ]
    } else {
      return loginErrors
    }
  }

  if (mfaToken !== '') {
    return (
      <BoxFormLayout>
        <MfaForm error={mfaError} isLoading={isLoadingMfa} onSubmit={onSubmitMfaCode} />
      </BoxFormLayout>
    )
  } else {
    if (window.opener !== null && !isSlateTimeout) {
      return (
        <BoxFormLayout>
          <SlateLoginForm onTimeout={() => setIsSlateTimeout(true)} />
        </BoxFormLayout>
      )
    } else {
      return (
        <BoxFormLayout>
          <LoginForm
            disabled={isLoadingLogin}
            errors={getErrors()}
            forgotPasswordLink={`${dashboardUrl}/reset-password`}
            isLoading={isLoadingLogin}
            onSubmit={submitCredentials}
          />
        </BoxFormLayout>
      )
    }
  }
}
